import { PartnerExtendedInfoResponse } from 'vopay';
import { useCallback, useRef, useState } from 'react';
import { api } from '@/services/api.config';

/** The business profile and contacts of a partner account, without the response envelope. */
export type PartnerExtendedInfo = Pick<PartnerExtendedInfoResponse, 'AccountInformationResponse' | 'AddressResponse'>;

/**
 * `GET /partner/extended-info` for the partner account whose bearer is given: its business profile,
 * address and the four contact sets. The route is self-scoped, so a parent reads one of its partner
 * accounts by signing the request with that account's own token.
 */
export default function useFetchPartnerExtendedInfo() {
  const [fetchingPartnerExtendedInfo, setFetchingPartnerExtendedInfo] = useState(false);
  const [partnerExtendedInfo, setPartnerExtendedInfo] = useState<PartnerExtendedInfo>();

  /**
   * Only the newest request may write to state. The partner page refetches as the user moves from
   * one partner account to another, and nothing cancels the request already in flight — so without
   * this an earlier response landing last would put one partner's profile under another's name.
   */
  const latestRequestId = useRef(0);

  const fetchPartnerExtendedInfo = useCallback(async (bearer: string) => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetchingPartnerExtendedInfo(true);
      const res = await api.fetchPartnerExtendedInfo(bearer);
      if (!res.Success) throw new Error(res.ErrorMessage);

      if (isLatest()) {
        setPartnerExtendedInfo({
          AccountInformationResponse: res.AccountInformationResponse,
          AddressResponse: res.AddressResponse,
        });
      }

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) setFetchingPartnerExtendedInfo(false);
    }
  }, []);

  const reset = useCallback(() => {
    // A reset supersedes whatever is in flight, so that response can no longer land on the cleared state.
    latestRequestId.current += 1;
    setPartnerExtendedInfo(undefined);
    setFetchingPartnerExtendedInfo(false);
  }, []);

  return { fetchingPartnerExtendedInfo, partnerExtendedInfo, fetchPartnerExtendedInfo, reset };
}
